import {useQuery, UseQueryResult} from '@tanstack/react-query';
import baseUrl from '../baseUrl';
import {handleResponse} from '../handleResponse';
import ProblemDetailsError from '../ProblemDetailsError';
import {useCreateAuthenticatedFetch} from '../useCreateAuthenticatedFetch';
import {useAuthContext} from '../../context/AuthContext';

export interface ClientUserProfile {
    isAuthenticated: boolean;
    username: string;
    name: string;
    groupMemberships: GroupMembershipDetail[];
    directRoles: RoleAssignmentDetail[];
    effectiveRoles: string[];
}

export interface GroupMembershipDetail {
    groupId: string;
    groupName: string;
    groupRoles: string[];
}

export interface RoleAssignmentDetail {
    roleName: string;
    roleDisplayName: string;
}

export const useGetUserProfile = (): UseQueryResult<
    ClientUserProfile,
    Error | ProblemDetailsError
> => {
    const {isAuthenticated} = useAuthContext();
    const authenticatedFetch = useCreateAuthenticatedFetch();

    // Only fetched once a session exists. An anonymous request gets a 200 with
    // an empty-roles stub, which used to race the real profile on a cold load
    // and bounce deep links to role-gated pages back to the home page.
    return useQuery({
        queryKey: ['user', 'profile', isAuthenticated],
        enabled: isAuthenticated,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/user/profile`
            );
            return handleResponse(response);
        }
    });
};
